import { useState, useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import { io } from 'socket.io-client';
import { Link, withRouter } from 'react-router-dom';

import Constants from '../../../constants/constants';
import goldenLock from '../../assets/icons/play-together/golden_lock.svg';
import { getAllMultiplayerGames } from '../../../modules/multiplayer_game';
import Dialog from '../Dialog';
import AuthService from '../../../services/auth/authService';
import { getRealm } from '../../utils/getRealm';
import AuthContext from '../../../context/AuthContext';

const LockIcon = styled.img`
  width: 48px;
  height: 48px;
  display: block;
  margin: 0 auto;
  margin-bottom: 15px;
`;

const Title = styled.div`
  font-size: 30px;
  font-weight: bold;
  margin-bottom: 20px;
`;

const Subtitle = styled.div(({ theme }) => {
  const { color } = theme;

  return `
    font-size: 15px;
    margin-bottom: 20px;
    color: ${color.grayscale['200']};
    width: 288px;
  `;
});

const GameSelect = styled.select(({ theme }) => {
  const { color } = theme;

  return ` 
    padding: 15px 20px;
    width: 288px;
    border: 1px solid ${color.grayscale['200']};
    box-sizing: border-box;
    border-radius: 8px;
    margin-bottom: 20px;
  `;
});

const Button = styled.button(({ theme }) => {
  const { color } = theme;

  return `
    width: 288px;
    padding: 15px 20px;
    border-radius: 8px;
    background: ${color.primary.regular};
    color: white;
  `;
});

const CreateNewRoomButton = styled.button(({ theme }) => {
  return `
    display: block;
    background: none;
    border: none;
    text-decoration: underline;
    cursor: pointer;
    color: ${theme.color.grayscale[100]};
    margin: 0 auto;
    margin-top: 10px;  
  `;
});

const RoomCreatedTitle = styled.div(({ theme }) => {
  const { type } = theme;

  return `
    font-size: ${type.size.regular};
    color: ${theme.color.grayscale['0']};
    margin-bottom: 10px;
  `;
});

const RoomCreatedSubtitle = styled.div(({ theme }) => {
  const { type } = theme;

  return `
    font-size: ${type.size.small};
    color: ${theme.color.grayscale['0']};
    margin-bottom: 15px;
  `;
});

const TogetherLink = styled.div`
  margin-bottom: 15px;
  font-size: 20px;
`;

const CodeInput = styled.input`
  flex-shrink: 3;
  font-size: 1.875rem;
  text-align: center;
  cursor: pointer;
  min-width: 0;
  vertical-align: center;
`;

const LoadingMessage = styled.div(({ theme }) => {
  return `
    font-size: ${theme.type.size.regular};
    text-align: center;
  `;
});

const ClipboardCopy = styled.button(({ theme }) => {
  return `
    flext-shrink: 1;
    cursor: pointer;
    text-align: center;
    border-radius: 8px;
    color: ${theme.color.grayscale['0']};
    padding: 5px 20px;
    height: 35px;
    min-width: 0;
    vertical-align: center;
  `;
});

const CodeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 180px;
  margin: 0 auto;
  margin-bottom: 15px;
  border: 1px solid;
  border-radius: 8px;
  padding: 10px;
`;

const PrivateGameDialog = (props) => {
  const { closeCallback, isOpen, location } = props;
  const socketClientRef = useRef();
  const keycloak = useContext(AuthContext);
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState(-1);
  const [roomCode, setRoomCode] = useState('');
  const [roomCreated, setRoomCreated] = useState(false);
  const [creatingRoom, setCreatingRoom] = useState(false);

  let codeInputRef;

  useEffect(() => {
    const socket = io(Constants.GAME_SERVER_URL, { transports: ['websocket'], reconnectionAttempts: 10 });

    const roomCreationComplete = (data) => {
      setRoomCode(data.room_code);
      setRoomCreated(true);
      setCreatingRoom(false);
    };

    socket.on('room_creation_result', roomCreationComplete);

    socketClientRef.current = socket;

    return () => {
      socket.removeAllListeners();
    };
  }, []);

  const copyCode = () => {
    codeInputRef.select();
    /* Select the text field */
    //this.codeElement.select();
    codeInputRef.setSelectionRange(0, 99999); /*For mobile devices*/

    /* Copy the text inside the text field */
    document.execCommand('copy');

    /* Alert the copied text */
    alert('copied to clipboard!');
  };

  const togetherURL = new URL(window.location.href);
  togetherURL.pathname = '/together/';

  const gameSelected = (e) => {
    setSelectedGameId(e.target.value);
  };

  const reset = () => {
    setSelectedGameId(-1);
    setRoomCode('');
    setRoomCreated(false);
  };

  const submitPrivateRoom = async () => {
    if (creatingRoom) {
      return;
    }

    if (selectedGameId === -1) {
      return;
    }

    setCreatingRoom(true);

    const realm = getRealm(window.location.host);
    let token = '';
    if (realm) {
      token = keycloak.token;
    } else {
      token = AuthService.getAuthToken(); //DONE: get token from local storage
    }

    const data = {
      game_name: selectedGameId,
      token: token,
    };

    if (realm) {
      data.realm = realm;
    }

    socketClientRef.current.emit('createRoom', data);
  };

  useEffect(() => {
    getAllMultiplayerGames().then((response) => {
      setGames(response.data.games);
    });
  }, []);

  const printGameSelect = () => {
    const gotGames = games.length > 0;

    return (
      <GameSelect defaultValue="" onChange={gameSelected}>
        <option value="" disabled>
          Choose a Game
        </option>

        {gotGames &&
          games.map((game, i) => {
            return (
              <option key={i} value={game.name}>
                {game.name}
              </option>
            );
          })}
      </GameSelect>
    );
  };

  return (
    <Dialog closeCallback={closeCallback} isOpen={isOpen}>
      {!roomCreated && (
        <>
          <LockIcon src={goldenLock} />

          <Title>Private Game</Title>

          <Subtitle>Invite a family member or a friend to a private game.</Subtitle>
          <div>{printGameSelect()}</div>

          {!creatingRoom && <Button onClick={submitPrivateRoom}>SUBMIT</Button>}

          {creatingRoom && (
            <LoadingMessage>
              <span>Creating room...</span>
              <i className="fa fa-spin fa-spinner"></i>
            </LoadingMessage>
          )}
        </>
      )}

      {roomCreated && (
        <>
          <RoomCreatedTitle>
            <strong>Success!</strong> Your game room has been created and is now open.
          </RoomCreatedTitle>

          <CodeContainer onClick={copyCode} title="Copy room code to clipboard">
            <CodeInput ref={(element) => (codeInputRef = element)} value={roomCode} readOnly />

            <ClipboardCopy>
              <i className="fa fa-clipboard"></i>
            </ClipboardCopy>
          </CodeContainer>

          <RoomCreatedSubtitle>People will be able to join your game by using the room code here:</RoomCreatedSubtitle>

          <TogetherLink>{togetherURL.href}</TogetherLink>

          <Link to={{ pathname: `/play/multiplayer/${roomCode}`, state: { from: location.pathname } }}>
            <Button>Join your room now!</Button>
          </Link>

          <CreateNewRoomButton onClick={reset}>Create a different room</CreateNewRoomButton>
        </>
      )}
    </Dialog>
  );
};

export default withRouter(PrivateGameDialog);
